<template>
  <button
    type="button"
    class="iq-disambiguation-option"
    :class="{ 'iq-disambiguation-option--selected': selected }"
    :aria-pressed="selected ? 'true' : 'false'"
    :disabled="disabled"
    data-testid="iq-disambiguation-option"
    @click="emit('select')"
  >
    <ExAvatar
      size="small"
      :picture="avatar || null"
      :first-name="nameParts.first"
      :last-name="nameParts.last"
    />
    <span class="iq-disambiguation-option__identity">
      <span class="iq-disambiguation-option__name">{{ label }}</span>
      <span v-if="positionTitle" class="iq-disambiguation-option__position">{{ positionTitle }}</span>
    </span>
  </button>
</template>

<script setup>
import { computed } from "vue";
import ExAvatar from "@components/common/ExAvatar.vue";

const props = defineProps({
  /** Employee display name. */
  label: {
    type: String,
    required: true,
  },
  /** Primary position title shown under the name. */
  positionTitle: {
    type: String,
    default: "",
  },
  /** Avatar image URL; initials are shown when empty. */
  avatar: {
    type: String,
    default: "",
  },
  /** Figma State=Selected. */
  selected: {
    type: Boolean,
    default: false,
  },
  /** Locked once a choice has been made. */
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["select"]);

const nameParts = computed(() => {
  const [first = "", ...rest] = String(props.label || "").trim().split(/\s+/);
  return { first, last: rest.length ? rest[rest.length - 1] : "" };
});
</script>

<style scoped>
.iq-disambiguation-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  box-sizing: border-box;
  text-align: left;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  cursor: pointer;
}

.iq-disambiguation-option:hover:not(:disabled) {
  background: #f9fafb;
}

.iq-disambiguation-option:focus-visible {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
}

.iq-disambiguation-option:disabled {
  cursor: default;
}

.iq-disambiguation-option--selected,
.iq-disambiguation-option--selected:hover:not(:disabled) {
  /* 2px border in the Selected frame: shrink padding by 1px so the box does not grow */
  padding: 9px 11px;
  background: #f9f5ff;
  border: 2px solid #7f56d9;
}

.iq-disambiguation-option__identity {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.iq-disambiguation-option__name {
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 15px;
  line-height: 22px;
  color: #111727;
  word-break: break-word;
}

.iq-disambiguation-option__position {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  color: #475467;
  word-break: break-word;
}
</style>
